import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Typography, Box } from '@mui/material';
import { getPlayerColor } from '../../../utils/PlayerColorUtils';

const EloProgressChart = ({ data, playerColorMap = {} }) => {
  // State to track screen width for responsive adjustments
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1000);
  
  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determine if we're on a mobile device
  const isMobile = windowWidth <= 768;
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div style={{ width: '100%', height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>No ELO data available</p>
      </div>
    );
  }

  // Process data to organize by race sequence
  const raceMap = {};
  const playerMap = {};
  
  // The API returns records in deterministic race order. Group by race ID rather
  // than timestamp: session completion writes every race in one transaction, so
  // timestamp grouping can collapse an entire Grand Prix into one chart point.
  data.forEach(record => {
    if (!raceMap[record.raceId]) {
      raceMap[record.raceId] = [];
    }
    raceMap[record.raceId].push(record);
    
    if (!playerMap[record.playerId]) {
      playerMap[record.playerId] = {
        id: record.playerId,
        name: `${record.firstName} ${record.lastName}`,
        color: playerColorMap[record.playerId] || getPlayerColor(record.playerId) // Use color map from parent or fallback
      };
    }
  });

  // Convert playerMap to array
  const players = Object.values(playerMap);
  
  // Object insertion order preserves the deterministic order returned by SQL.
  const sortedRaces = Object.keys(raceMap);

  // Prepare chart data
  const chartData = [];
  
  // Track the latest ELO for each player and find min/max ELO values
  const latestPlayerElos = {};
  
  // Find min and max ELO values for y-axis scaling
  let minElo = Infinity;
  let maxElo = -Infinity;
  
  data.forEach(record => {
    if (record.elo < minElo) minElo = record.elo;
    if (record.elo > maxElo) maxElo = record.elo;
  });
  
  // Add padding to the min and max ELO values
  const yAxisMin = Math.floor(minElo - 100);
  const yAxisMax = Math.ceil(maxElo + 100);
  
  sortedRaces.forEach((raceId, raceIndex) => {
    const raceRecords = raceMap[raceId];
    const dataPoint = { name: `${raceIndex + 1}: ${raceRecords[0].course}` };
    
    // For each race, find the ELO for each player
    players.forEach(player => {
      const playerRecord = raceRecords.find(record => record.playerId === player.id);
      
      if (playerRecord) {
        dataPoint[`player_${player.id}`] = playerRecord.elo;
        latestPlayerElos[player.id] = playerRecord.elo;
      } else {
        // If player didn't race this course, carry forward their previous ELO
        dataPoint[`player_${player.id}`] = latestPlayerElos[player.id] || null;
      }
    });
    
    chartData.push(dataPoint);
  });

  // Custom tooltip to show player ELOs when hovering
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Sort entries by ELO (higher ELO first)
      const sortedEntries = [...payload]
        .filter(entry => entry.value !== undefined && (entry.value || entry.value === 0))
        .sort((a, b) => b.value - a.value);
      
      return (
        <div className="custom-tooltip" style={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.8)', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '5px',
          color: 'white'
        }}>
          <p className="label" style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{label}</p>
          <div>
            {sortedEntries.map((entry, index) => {
              const playerName = entry.name;
              return (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
                  <div style={{ 
                    width: '10px', 
                    height: '10px', 
                    backgroundColor: entry.stroke, 
                    marginRight: '5px' 
                  }}></div>
                  <span style={{ fontWeight: index === 0 ? 'bold' : 'normal' }}>
                    {index + 1}. {playerName}: {entry.value} ELO
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: '100%', height: '400px', position: 'relative' }}>
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ marginTop: 0, marginBottom: 0 }}>
        <Typography variant="h6" sx={{ fontFamily: '"Press Start 2P", sans-serif', fontWeight: 'bold', fontSize: '1rem' }}>
          ELO Progression
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" fill='black' fillOpacity={0.6}/>
          <XAxis 
            dataKey="name" 
            tick={props => {
              const { x, y, payload } = props;
              // Standard font size for axis values
              const fontSize = 11;
              
              // For all devices, show just the race number
              const displayText = payload.value.split(':')[0];
                
              return (
                <g transform={`translate(${x},${y})`}>
                  <text 
                    x={0} 
                    y={0} 
                    dy={10} 
                    textAnchor="middle" 
                    fill="#6B7280" 
                    fontSize={fontSize}
                  >
                    {displayText}
                  </text>
                </g>
              );
            }}
            height={isMobile ? 30 : 40}
            interval={0} // Show all ticks to ensure alignment
            tickMargin={5} // Reduced margin to bring labels closer
            label={{ value: 'Race Number', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 14 } }}
            scale="point" // Use point scale for better alignment
            padding={{ left: 0, right: 0 }} // No extra padding on sides
            axisLine={true}
            tickLine={true}
          />
          <YAxis
            tick={{ fill: '#6B7280', fontSize: 11 }}
            width={35}
            domain={[yAxisMin, yAxisMax]}
            label={{ value: 'ELO', angle: -90, position: 'insideLeft', dx: -12, style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 14 } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="top"
            height={26}
            wrapperStyle={{
              paddingBottom: isMobile ? 15 : 10,
              fontSize: 11,
              marginTop: -3
            }}
            iconSize={8} 
            layout="horizontal"
            align="center"
          />
          
          {players.map(player => (
            <Line
              key={player.id}
              type="monotone"
              dataKey={`player_${player.id}`}
              name={player.name}
              stroke={player.color}
              strokeWidth={2}
              activeDot={{ r: 5 }}
              dot={false}
              connectNulls={true} // Connect dots even if there are missing values
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};



export default EloProgressChart;
